import React from 'react';
import { useStats } from '../hooks/useData';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#6366f1','#0ea5e9','#22c55e','#f59e0b','#a855f7','#ef4444'];

export function Analytics() {
  const { stats, loading } = useStats();

  if (loading) return (
    <div className="page"><div className="empty"><div className="empty-title">Loading analytics...</div></div></div>
  );

  const jxData = Object.entries(stats?.byJurisdiction ?? {}).map(([key, count]) => ({
    name: key.replace('_', ' ').replace('US CAN SPAM', 'USA').replace('EU GDPR', 'EU').replace('UK PECR', 'UK'),
    value: count as number,
  }));

  const sourceData = (stats?.topSources ?? []).map(s => ({
    name: s.source,
    count: s.count,
  }));

  return (
    <div className="page">
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 16 }}>Analytics</div>
        <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)', marginTop: 2 }}>
          Visitor intelligence insights
        </div>
      </div>

      <div className="grid2" style={{ marginBottom: 16 }}>
        {/* Traffic sources */}
        <div className="card">
          <div className="card-title">Traffic Sources</div>
          {sourceData.length === 0 ? (
            <div className="empty" style={{ padding: 24 }}>
              <div className="empty-title">No source data yet</div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={sourceData} margin={{ top: 4, right: 4, bottom: 4, left: -20 }}>
                <XAxis dataKey="name" tick={{ fill: 'var(--text3)', fontSize: 11, fontFamily: 'var(--mono)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'var(--text3)', fontSize: 11, fontFamily: 'var(--mono)' }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: 'var(--text)' }}
                  itemStyle={{ color: 'var(--accent2)' }}
                />
                <Bar dataKey="count" fill="var(--accent)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Jurisdictions */}
        <div className="card">
          <div className="card-title">Visitor Jurisdictions</div>
          {jxData.length === 0 ? (
            <div className="empty" style={{ padding: 24 }}>
              <div className="empty-title">No jurisdiction data yet</div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
              <ResponsiveContainer width="50%" height={180}>
                <PieChart>
                  <Pie data={jxData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                    {jxData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ flex: 1 }}>
                {jxData.map((d, i) => (
                  <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 2, background: COLORS[i % COLORS.length], flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: 'var(--text2)', flex: 1 }}>{d.name}</span>
                    <span style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--text3)' }}>{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Top search terms */}
      <div className="card">
        <div className="card-title">Top Search Terms</div>
        {(stats?.topSearchTerms ?? []).length === 0 ? (
          <div className="empty" style={{ padding: 24 }}>
            <div className="empty-title">No search term data yet</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(stats?.topSearchTerms ?? []).map((t, i) => (
              <div key={t.term} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', width: 20 }}>#{i + 1}</span>
                <div style={{ flex: 1, height: 28, background: 'var(--bg3)', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
                  <div style={{
                    position: 'absolute', inset: 0,
                    width: `${(t.count / ((stats?.topSearchTerms?.[0]?.count ?? 1)))*100}%`,
                    background: 'var(--accent)', opacity: 0.2, borderRadius: 4,
                  }} />
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', padding: '0 12px', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 13, color: 'var(--text)' }}>{t.term}</span>
                    <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text3)' }}>{t.count}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
